// DD-3700: format checks for a lab's payment details, so the lab sees a mistake while typing.
// The backend repeats them (shared-denthub-code invoice-payment.ts) and is the authority.

export enum PaymentBoxAccount {
    BANKGIRO = 'BANKGIRO',
    PLUSGIRO = 'PLUSGIRO',
}

const digitsOnly = (value: string) => value.replace(/[\s-]/g, '');

const hasValidLuhnCheckDigit = (digits: string) => {
    let sum = 0;
    for (let index = 0; index < digits.length; index++) {
        let digit = Number(digits[digits.length - 1 - index]);
        if (index % 2 === 1) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }
        sum += digit;
    }
    return sum % 10 === 0;
};

export const isValidBankgiro = (value: string) => {
    const digits = digitsOnly(value);
    return /^\d{7,8}$/.test(digits) && hasValidLuhnCheckDigit(digits);
};

export const isValidPlusgiro = (value: string) => {
    const digits = digitsOnly(value);
    return /^\d{2,8}$/.test(digits) && hasValidLuhnCheckDigit(digits);
};

export const isValidIban = (value: string) => {
    const iban = value.replace(/\s/g, '').toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) {
        return false;
    }
    const numeric = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (letter) => String(letter.charCodeAt(0) - 55));
    let remainder = 0;
    for (const digit of numeric) {
        remainder = (remainder * 10 + Number(digit)) % 97;
    }
    return remainder === 1;
};

export const isValidBic = (value: string) =>
    /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value.replace(/\s/g, '').toUpperCase());

/** Swedish labs can be paid by bankgiro and plusgiro; every lab by IBAN and BIC. */
export const hasSwedishGiroFields = (country?: string) => (country || 'SE').toUpperCase() === 'SE';

/** Norwegian labs are paid to their account number (kontonummer), with a KID if they have one. */
export const hasNorwegianPaymentFields = (country?: string) => (country || '').toUpperCase() === 'NO';

/** Danish labs are paid to reg.nr. + account number, and with the FI code line if they have FI. */
export const hasDanishPaymentFields = (country?: string) => (country || '').toUpperCase() === 'DK';

/** Finnish labs choose their payment reference format and set late interest / complaint period. */
export const hasFinnishPaymentFields = (country?: string) => (country || '').toUpperCase() === 'FI';

/** German labs print a Steuernummer, company register details and the § 19 UStG status. */
export const hasGermanCompanyFields = (country?: string) => (country || '').toUpperCase() === 'DE';

/** Dutch labs can be in the small business scheme (kleineondernemersregeling, KOR). */
export const hasDutchCompanyFields = (country?: string) => (country || '').toUpperCase() === 'NL';

export enum FinnishReferenceFormat {
    NATIONAL = 'NATIONAL',
    RF = 'RF',
}

export enum FiCardType {
    FI_71 = '71',
    FI_73 = '73',
    FI_75 = '75',
}

export const isValidDanishRegistrationNumber = (value: string) => /^\d{4}$/.test(value.replace(/\s/g, ''));

export const isValidDanishAccountNumber = (value: string) => /^\d{1,10}$/.test(value.replace(/[\s-]/g, ''));

export const isValidFiCreditorNumber = (value: string) => /^\d{8}$/.test(value.replace(/\s/g, ''));

export enum KidCheckDigitMethod {
    MOD10 = 'MOD10',
    MOD11 = 'MOD11',
}

export const KID_MIN_LENGTH = 2;
export const KID_MAX_LENGTH = 25;

const NORWEGIAN_ACCOUNT_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

/** Norwegian account number: 11 digits (written 1234.56.78903), the last a MOD11 check digit. */
export const isValidNorwegianAccountNumber = (value: string) => {
    const digits = value.replace(/[\s.]/g, '');
    if (!/^\d{11}$/.test(digits)) {
        return false;
    }
    const sum = NORWEGIAN_ACCOUNT_WEIGHTS.reduce((total, weight, index) => total + weight * Number(digits[index]), 0);
    const remainder = sum % 11;
    const checkDigit = remainder === 0 ? 0 : 11 - remainder;
    return checkDigit !== 10 && checkDigit === Number(digits[10]);
};
